import { AEMImageData } from "@components/AEMImage"
import { MediaPositionType } from "@components/ui/CompoundSideMedia/CompoundSideMedia"
import { LinkItem } from "@models/link.model"
import { Brand } from "@utils/constants.util"
import { AEMResponseMapper } from "@AEM/AEMContentMapper"
import { DeckContent } from "./AEMDeckplan.model"
import { DateRangeOptions } from "@models/seeOurShip.model"
import { AEMPromotionOffer } from "./AEMResponse.model"

export enum EntityPathParams {
	BRAND = "${BRAND}",
	SHIP = "-${SHIP}",
	OFFICE = "${OFFICE}"
}

export interface AEMCache {
	[locale: string]: {
		[page: string]: AEMPage
	}
}

export interface AEMPage {
	items: [
		{
			backgroundHeroDescription?: string
			title?: string
			brand?: BrandHeroType
			id?: string
			components: AEMComponentType[]
			items?: AEMComponentType[]
			detailModel?: AEMComponentType
		}
	]
}

export interface AEMLeafNode {
	children?: {
		items: (AEMSectionNode | AEMLeafNode)[]
	}
	label: string
	description: AEMDescriptionType
	toolCode: string
	link: string
	type: string
	section: boolean
	icon?: string
	appId?: string
	brand?: string
}

export interface AEMSectionNode {
	label: string
	description: AEMDescriptionType
	section: boolean
	children: {
		items?: (AEMSectionNode | AEMLeafNode)[]
	}
}

export interface AEMDescriptionType {
	markdown: string
	plaintext: string
}

export interface AEMNavigation {
	[key: number]: {
		children: {
			items: (AEMSectionNode | AEMLeafNode)[]
		}
	}
}

export interface AEMAlert {
	data: {
		componentAlertBannerList?: { items: unknown[] }
		componentNotificationsList?: { items: unknown[] }
	}
}

export interface AEMComponentType {
	type: string
	query?: string
	// biome-ignore lint/suspicious/noExplicitAny: Used any since any type of key value pair could come from AEM
	[key: string]: any
}

export interface AEMContent {
	// biome-ignore lint/suspicious/noExplicitAny: Used any since any type of key value pair could come from AEM
	[key: string]: any
}

export interface AEMContentParams {
	locale?: string
	role?: string
	office?: string
	country?: string
	path?: string
	brandCode?: string
	enabled?: boolean
	collection?: string
	offerName?: string
	offerFor?: string
	ship?: string
	destination?: string
	offerType?: string
	brand?: string
	tag?: string
	contentPath?: string
	shipCode?: string
	profile?: string
	stateroomSubtypeCode?: string
	today?: string
	name?: string
	"header.brand"?: string
	"header.application"?: string
	"header.language"?: string
	startDate?: string
}

export interface FilterOption {
	value: string
	label: string
}

export interface AEMOptions {
	queryParams?: AEMContentParams
	uniqueId?: string
	pathParams?: string[]
	brand?: Brand
	office?: string
	ship?: string
	mapper?: AEMResponseMapper
	enabled?: boolean
	destinationCode?: string
	path?: string
}

export enum AEMPageType {
	HOME = "home",
	LOGIN = "login",
	PASSWORD_RESET_LOGIN = "password-reset-login",
	AGENCY_REGISTRATION_HOME = "agency-registration-home",
	AGENCY_EMAIL_VERIFICATION = "agency-email-verification",
	AGENCY_EMAIL_VERIFICATION_SENT = "agency-email-verification-sent",
	AGENCY_SALES_CONTRACT = "agency-sales-contract",
	FOOTER = "footer",
	PROMOTIONS_HOME = "promotions-home",
	FEATURED_OFFERS = "featured-offers",
	ARCHIVED_OFFERS = "archived-offers",
	OFFERS = "offers-name",
	EXPIRED_OFFERS = "expired-offers",
	SEE_OUR_SHIPS = "see-our-ships",
	LANGUAGES = "languages",
	DICTIONARY = "dictionary",
	ALERTS = "alerts",
	NOTIFICATIONS = "notifications",
	NAVIGATION_FOOTER = "navigation-footer",
	NAVIGATION_GLOBAL = "navigation-global",
	NAVIGATION_SIDEBAR = "navigation-sidebar",
	NAVIGATION_HOME = "navigation-home",
	PAGES_HOME = "pages-home",
	PAGES_PRIVACY_POLICY = "privacy-policy",
	PAGES_TRAVEL_AGENT_GUIDELINES = "travel-agent-guidelines",
	PAGES_TERMS_OF_USE = "terms-of-use",
	PAGES_INSIGHTS_ALERT_CENTER = "insights-alert-center",
	PAGES_INSIGHTS_COUPON_DETAILS = "insights-coupon-application-details",
	PAGES_INSIGHTS_FUTURE_COMPENSATION = "insights-future-compensation",
	PAGES_INSIGHTS_INDIVIDUAL_RESERVATIONS = "insights-individual-reservations",
	PAGES_INSIGHTS_NPC_TRANSMISSION = "insights-npc-transmission-details",
	PAGES_INSIGHTS_OVERVIEW = "insights-overview",
	PAGES_INSIGHTS_AUTOMATED_EMAILS = "insights-set-up-automated-emails",
	PAGES_INSIGHTS_GROUP_RESERVATIONS = "insights-group-reservations",
	PAGES_INSIGHTS_ONBOARD_SALES = "insights-onboard-sales",
	PAGES_INSIGHTS_CASINO_RESERVATIONS = "insights-casino-reservations",
	PAGES_INSIGHTS_VIEW_BRANCH = "insights-view-another-branch",
	PAGES_PROMOTIONS_BRAND_LANDING_ROYAL = "promotions-brand-landing-royal",
	PAGES_PROMOTIONS_OCTOBER_OFFER = "promotions-october-offer",
	PAGES_PROMOTIONS_SEARCH_RESULTS = "promotions-search-results",
	PAGES_SEARCH_RESULTS = "search-results",
	TAGS = "tags",
	ICONS = "icons",
	SEE_OUR_SHIPS_OVERVIEW = "see-our-ships-overview",
	SEE_OUR_SHIPS_ENTERTAINMENT = "see-our-ships-entertainment",
	SEE_OUR_SHIPS_ACTIVITIES = "see-our-ships-activities",
	SEE_OUR_SHIPS_BAR_AND_LOUNGE = "see-our-ships-bar-and-lounge",
	SEE_OUR_SHIPS_ROOMS = "see-our-ships-rooms",
	SEE_OUR_SHIPS_ROOMS_SUBCATEGORY = "see-our-ships-rooms-subcategory",
	SEE_OUR_SHIPS_FLEET = "see-our-ships-fleet",
	SEE_OUR_SHIPS_DINING = "see-our-ships-dining",
	SEE_OUR_SHIPS_SPECIALTY_DINING = "see-our-ships-specialty-dining",
	SEE_OUR_SHIPS_COMPLIMENTARY_DINING = "see-our-ships-complimentary-dining",
	DINING = "dining",
	DINING_MANAGEMENT_HOME = "dining-management-home",
	DINNING_INTERFACE = "dining-interface",
	GROUP_DINING_SUMMARY = "group-dining-summary",
	GROUP_DINING_TABLE_MANAGEMENT = "group-dining-table-management",
	SEE_OUR_SHIPS_DECK_PLANS = "see-our-ships-deck-plans",
	SEE_OUR_SHIPS_NEIGHBOURHOODS = "see-our-ships-neighbourhoods",
	SOS_ENTITY_ROOMS = "rooms",
	STATEROOM_SUBTYPE = "stateroom-subtype-content",
	ENTERTAIMENT = "entertainment",
	STATEROOM_SUBTYPE_DETAIL = "stateroom-subtype-detail-content",
	DECKPLAN = "deck-content",
	SHIP_PROFILE_CONTENT = "ship-profile-content",
	AGENT_REGISTRATION_HOME = "agent-registration-home",
	AGENT_REGISTRATION_FOUND = "agent-registration-found",
	AGENT_REGISTRATION_EMAIL = "agent-registration-email",
	AGENT_REGISTRATION_VERIFICATION_SUCCESS = "agent-email-verification-success",
	AGENT_WELCOME = "agent-welcome",
	APP_SETTINGS = "app-settings",
	SEARCH_ITINERARY_HOME = "search-itinerary-home",
	CERTIFICATE_HOME = "certificate-home",
	ALERTS_HOME = "alerts-home",
	SEARCH_ITINERARY_DETAIL = "search-itinerary-detail",
	USER_MANAGEMENT = "user-management",
	GROUP_TRAVEL_HUB = "group-travel-hub",
	PROFILE = "profile-info",
	TRAVEL_DOCS = "travel-docs",
	REDUCED_RATES = "reduced-rates",
	ACCOUNT_FORGOT = "account-forgot",
	CERTIFICATES = "certificates",
	DESTINATIONS = "destinations",
	TA_TERMS_AND_CONDITIONS_HOME = "ta-terms-and-conditions-home",
	TA_TERMS_AND_CONDITIONS = "ta-terms-and-conditions",
	TA_TRAVEL_PARTNER_ELIGIBILITY_REQUIREMENTS = "ta-travel-partner-eligibility-requirements",
	TA_IMPORTANT_INFORMATION = "ta-important-information",
	GROUP_TRAVEL_HUB_ABOUT_BOOKINGS = "group-travel-hub-about-group-bookings",
	GROUP_TRAVEL_HUB_POLICIES = "group-travel-hub-policies",
	GROUP_TRAVEL_HUB_REQUEST_FORM_LINKS = "group-travel-hub-request-form-links",
	GROUP_TRAVEL_HUB_TC = "group-travel-hub-tc",
	GROUP_TRAVEL_HUB_POLICIES_TC_ROYAL = "group-travel-hub-policies-tc-royal",
	GROUP_TRAVEL_HUB_POLICIES_TC_CELEBRITY = "group-travel-hub-policies-tc-celebrity",
	// TODO: This pages are not yet implemented in AEM, these are placeholder pages for dev work
	MICROSITE_OVERVIEW = "microsite-overview",
	MICROSITE_GALLERY = "microsite-gallery",
	MICROSITE_EVENTS = "microsite-events",
	MICROSITE_ABOUT = "microsite-about",
	MICROSITE_REGISTER_COFFEE_TALK = "microsite-register",
	MICROSITE_COFFEE_TALK_SUCCESS = "microsite-coffee-talk-success",
	MICROSITE_SEAS_TO_SUCCESS = "microsite-seas-to-success",
	MICROSITE_ASSET_LIBRARY = "microsite-asset-library",
	MICROSITE_ASSET_LIBRARY_FILES = "microsite-asset-library-files",
	MICROSITE_NO_UPCOMING_EVENTS = "microsite-no-upcoming-events",
	MICROSITE_NO_GALLERY = "microsite-no-gallery",
	MICROSITE_MARKETING_ASSETS = "microsite-marketing-assets",
	MICROSITE_WEBINARS = "webinars",
	COUNTDOWN_BANNER = "countdown-banner",
	ONE_STOP_PRICING = "one-stop-pricing",
	CONTACT_US = "contact-us",
	QUICK_REFERENCE_HOME = "quick-reference-home",
	QUICK_REFERENCE_POPULAR_TOPICS = "popular-topics",
	QUICK_REFERENCE_TOP_LEVEL_HIERARCHY = "top-level-hierarchy",
	QUICK_REFERENCE_TOPIC_DETAIL = "quick-reference-topic-detail",
	QUICK_REFERENCE_RESOURCE_LINKS = "quick-reference-resource-links",
	GLOBAL_SEARCH_LANDING = "global-search-landing",
	MARKETING_CENTER = "mmc-home"
}

export interface AEMPagePath {
	id: AEMPageType
	path: string
}

export const AEMPagePaths: Record<AEMPageType, string> = {
	[AEMPageType.HOME]: "/home",
	[AEMPageType.PAGES_HOME]: "/pages/home",
	[AEMPageType.LOGIN]: "/login",
	[AEMPageType.AGENCY_REGISTRATION_HOME]: "/pages/agency-registration-home",
	[AEMPageType.AGENCY_EMAIL_VERIFICATION]: "/pages/agency-email-verification",
	[AEMPageType.PASSWORD_RESET_LOGIN]: "/pages/password-reset-login",
	[AEMPageType.AGENCY_EMAIL_VERIFICATION_SENT]:
		"/pages/agency-email-verification-sent",
	[AEMPageType.AGENCY_SALES_CONTRACT]: "/pages/agency-sales-contract",
	[AEMPageType.FOOTER]: "/footer",
	[AEMPageType.PROMOTIONS_HOME]: "/pages/promotions-home",
	[AEMPageType.FEATURED_OFFERS]: "/pages/featured-offers",
	[AEMPageType.ARCHIVED_OFFERS]: "/pages/archived-offers",
	[AEMPageType.OFFERS]: "/offers",
	[AEMPageType.EXPIRED_OFFERS]: "/expired-offers",
	[AEMPageType.SEE_OUR_SHIPS]: "/pages/sos-home",
	[AEMPageType.LANGUAGES]: "/languages",
	[AEMPageType.DICTIONARY]: "/dictionary",
	[AEMPageType.ALERTS]: "/alerts",
	[AEMPageType.NOTIFICATIONS]: "/notifications",
	[AEMPageType.NAVIGATION_FOOTER]: "/navigation/footer",
	[AEMPageType.NAVIGATION_GLOBAL]: "/navigation/global",
	[AEMPageType.NAVIGATION_SIDEBAR]: "/navigation/sidebar",
	[AEMPageType.NAVIGATION_HOME]: "/navigation/home",
	[AEMPageType.PAGES_PRIVACY_POLICY]: "/pages/privacy-policy",
	[AEMPageType.PAGES_TRAVEL_AGENT_GUIDELINES]: "/pages/travel-agent-guidelines",
	[AEMPageType.PAGES_TERMS_OF_USE]: "/pages/terms-of-use",
	[AEMPageType.PAGES_INSIGHTS_ALERT_CENTER]: "/pages/insights-alert-center",
	[AEMPageType.PAGES_INSIGHTS_COUPON_DETAILS]:
		"/pages/insights/coupon-application-details",
	[AEMPageType.PAGES_INSIGHTS_FUTURE_COMPENSATION]:
		"/pages/insights-future-compensation",
	[AEMPageType.PAGES_INSIGHTS_INDIVIDUAL_RESERVATIONS]:
		"/pages/insights-individual-reservations",
	[AEMPageType.PAGES_INSIGHTS_NPC_TRANSMISSION]:
		"/pages/insights-npc-transmission-details",
	[AEMPageType.PAGES_INSIGHTS_OVERVIEW]: "/pages/insights-overview",
	[AEMPageType.PAGES_INSIGHTS_AUTOMATED_EMAILS]:
		"/pages/insights/set-up-automated-emails",
	[AEMPageType.PAGES_INSIGHTS_GROUP_RESERVATIONS]:
		"/pages/insights-group-reservations",
	[AEMPageType.PAGES_INSIGHTS_ONBOARD_SALES]: "/pages/insights-onboard-sales",
	[AEMPageType.PAGES_INSIGHTS_CASINO_RESERVATIONS]:
		"/pages/insights-casino-reservations",
	[AEMPageType.PAGES_INSIGHTS_VIEW_BRANCH]:
		"/pages/insights/view-another-branch",
	[AEMPageType.PAGES_PROMOTIONS_BRAND_LANDING_ROYAL]:
		"/pages/promotions-brand-landing-royal",
	[AEMPageType.PAGES_PROMOTIONS_OCTOBER_OFFER]:
		"/pages/promotions-offers/october-2024-monthlong-offer",
	[AEMPageType.PAGES_PROMOTIONS_SEARCH_RESULTS]:
		"/pages/promotions-search-results",
	[AEMPageType.TAGS]: "/tags",
	[AEMPageType.ICONS]: "/icons",
	[AEMPageType.PAGES_SEARCH_RESULTS]: "/pages/search-results",
	[AEMPageType.SEE_OUR_SHIPS_OVERVIEW]: `/pages/${EntityPathParams.BRAND}-overview${EntityPathParams.SHIP}`,
	[AEMPageType.SEE_OUR_SHIPS_FLEET]: `/pages/${EntityPathParams.BRAND}-fleet`,
	[AEMPageType.SEE_OUR_SHIPS_DINING]: `/pages/${EntityPathParams.BRAND}-dining${EntityPathParams.SHIP}`,
	[AEMPageType.SEE_OUR_SHIPS_SPECIALTY_DINING]: `/pages/${EntityPathParams.BRAND}-specialty-dining${EntityPathParams.SHIP}`,
	[AEMPageType.SEE_OUR_SHIPS_COMPLIMENTARY_DINING]: `/pages/${EntityPathParams.BRAND}-complimentary-dining${EntityPathParams.SHIP}`,
	[AEMPageType.SEE_OUR_SHIPS_ENTERTAINMENT]: `/pages/${EntityPathParams.BRAND}-entertainment${EntityPathParams.SHIP}`,
	[AEMPageType.SEE_OUR_SHIPS_ACTIVITIES]: `/pages/${EntityPathParams.BRAND}-activities${EntityPathParams.SHIP}`,
	[AEMPageType.SEE_OUR_SHIPS_BAR_AND_LOUNGE]: `/pages/${EntityPathParams.BRAND}-bars-lounges${EntityPathParams.SHIP}`,
	[AEMPageType.SEE_OUR_SHIPS_ROOMS]: `/pages/${EntityPathParams.BRAND}-rooms${EntityPathParams.SHIP}`,
	[AEMPageType.SEE_OUR_SHIPS_ROOMS_SUBCATEGORY]: `/pages/${EntityPathParams.BRAND}-rooms-subcategory${EntityPathParams.SHIP}`,
	[AEMPageType.DINING]: "/dining",
	[AEMPageType.SEE_OUR_SHIPS_DECK_PLANS]: `/pages/${EntityPathParams.BRAND}-deck-plan${EntityPathParams.SHIP}`,
	[AEMPageType.SEE_OUR_SHIPS_NEIGHBOURHOODS]: `/pages/${EntityPathParams.BRAND}-neighbourhoods${EntityPathParams.SHIP}`,
	[AEMPageType.SOS_ENTITY_ROOMS]: "/rooms",
	[AEMPageType.STATEROOM_SUBTYPE]: "/stateroom-subtype-content",
	[AEMPageType.ENTERTAIMENT]: "/entertainment",
	[AEMPageType.DECKPLAN]: "/deck-content",
	[AEMPageType.STATEROOM_SUBTYPE_DETAIL]: "/stateroom-subtype-detail-content",
	[AEMPageType.SHIP_PROFILE_CONTENT]: "/ship-profile-summary",
	[AEMPageType.AGENT_REGISTRATION_HOME]: "/pages/agent-registration-home",
	[AEMPageType.AGENT_REGISTRATION_FOUND]: "/pages/agent-registration-found",
	[AEMPageType.AGENT_REGISTRATION_EMAIL]: "/pages/agent-registration-email",
	[AEMPageType.AGENT_REGISTRATION_VERIFICATION_SUCCESS]:
		"/pages/agent-email-verification-sent",
	[AEMPageType.AGENT_WELCOME]: "/pages/agent-welcome",
	[AEMPageType.APP_SETTINGS]: "/app-settings",
	[AEMPageType.SEARCH_ITINERARY_HOME]: "/pages/search-itinerary-home",
	[AEMPageType.CERTIFICATE_HOME]: "/pages/certificate-home",
	[AEMPageType.ALERTS_HOME]: "/pages/alerts-home",
	[AEMPageType.SEARCH_ITINERARY_DETAIL]: "/pages/search-itinerary-detail",
	[AEMPageType.DINING_MANAGEMENT_HOME]: "/pages/dining-management-home",
	[AEMPageType.DINNING_INTERFACE]: "/pages/dining-interface",
	[AEMPageType.GROUP_DINING_SUMMARY]: "/pages/group-dining-summary",
	[AEMPageType.GROUP_DINING_TABLE_MANAGEMENT]:
		"/pages/group-dining-table-management",
	[AEMPageType.USER_MANAGEMENT]: "/pages/user-management",
	[AEMPageType.GROUP_TRAVEL_HUB]: "/pages/group-travel-hub",
	[AEMPageType.PROFILE]: "/pages/profile-info",
	[AEMPageType.TRAVEL_DOCS]: "/pages/travel-docs",
	[AEMPageType.REDUCED_RATES]: "/pages/reduced-rates",
	[AEMPageType.ACCOUNT_FORGOT]: "/pages/account-forgot",
	[AEMPageType.CERTIFICATES]: "/certificates",
	[AEMPageType.DESTINATIONS]: "/destinations",
	[AEMPageType.TA_TERMS_AND_CONDITIONS_HOME]:
		"/pages/TA-Terms-and-Conditions-Home",
	[AEMPageType.TA_TERMS_AND_CONDITIONS]: "/pages/TA-Terms-and-Conditions",
	[AEMPageType.TA_TRAVEL_PARTNER_ELIGIBILITY_REQUIREMENTS]:
		"/pages/TA-Travel-Partner-Eligibility-Requirements",
	[AEMPageType.TA_IMPORTANT_INFORMATION]: "/pages/TA-Important-Information",
	[AEMPageType.GROUP_TRAVEL_HUB_ABOUT_BOOKINGS]: `/pages/${EntityPathParams.BRAND}-Group-Bookings-Home`,
	[AEMPageType.GROUP_TRAVEL_HUB_POLICIES]: `/pages/${EntityPathParams.BRAND}-Travelhub-Policies`,
	[AEMPageType.GROUP_TRAVEL_HUB_REQUEST_FORM_LINKS]:
		"/pages/request-form-links?locale=en_us",
	[AEMPageType.GROUP_TRAVEL_HUB_TC]: "/pages/travelhub-TC",
	[AEMPageType.GROUP_TRAVEL_HUB_POLICIES_TC_ROYAL]:
		"/pages/R-Travelhub-Policies-TC",
	[AEMPageType.GROUP_TRAVEL_HUB_POLICIES_TC_CELEBRITY]:
		"/pages/C-Travelhub-Policies-TC",
	[AEMPageType.MICROSITE_OVERVIEW]: `/pages/${EntityPathParams.BRAND}-trade-home-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_GALLERY]: `/media-gallery/${EntityPathParams.BRAND}-media-gallery-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_EVENTS]: "/events",
	[AEMPageType.MICROSITE_WEBINARS]: "/webinars",
	[AEMPageType.MICROSITE_ABOUT]: `/pages/${EntityPathParams.BRAND}-trade-about-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_REGISTER_COFFEE_TALK]: `/pages/${EntityPathParams.BRAND}-coffee-talk-registration-hero-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_COFFEE_TALK_SUCCESS]: `/pages/${EntityPathParams.BRAND}-coffee-talk-success-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_SEAS_TO_SUCCESS]: `/pages/${EntityPathParams.BRAND}-seas-to-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_ASSET_LIBRARY]: `/pages/${EntityPathParams.BRAND}-trade-assets-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_NO_UPCOMING_EVENTS]: `/pages/${EntityPathParams.BRAND}-trade-no-events-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_NO_GALLERY]: `/pages/${EntityPathParams.BRAND}-trade-no-media-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_MARKETING_ASSETS]: `/pages/${EntityPathParams.BRAND}-trade-marketing-share-${EntityPathParams.OFFICE}`,
	[AEMPageType.MICROSITE_ASSET_LIBRARY_FILES]: "/assets",
	[AEMPageType.COUNTDOWN_BANNER]: "/countdown-banners",
	[AEMPageType.QUICK_REFERENCE_HOME]: `/pages/quick-reference-home-${EntityPathParams.BRAND}`,
	[AEMPageType.QUICK_REFERENCE_POPULAR_TOPICS]:
		"/quick-references/popular-topics",
	[AEMPageType.QUICK_REFERENCE_TOP_LEVEL_HIERARCHY]:
		"/quick-references/top-level-hierarchy",
	[AEMPageType.QUICK_REFERENCE_TOPIC_DETAIL]: "/quick-references/topics",
	[AEMPageType.QUICK_REFERENCE_RESOURCE_LINKS]:
		"/quick-references/resource-links",
	[AEMPageType.ONE_STOP_PRICING]: `/pages/one-stop-pricing-${EntityPathParams.BRAND}`,
	[AEMPageType.CONTACT_US]: `/pages/${EntityPathParams.BRAND}-contact-us`,
	[AEMPageType.GLOBAL_SEARCH_LANDING]: "/pages/global-search-landing",
	[AEMPageType.MARKETING_CENTER]: "/pages/mmc-home"
}

export enum AEMComponentMapper {
	WELCOME = "ComponentWelcomeModel",
	SEARCHABLE_LONG_FORM_CONTENT = "ComponentSearchableLongFormContentModel",
	ESPRESSO_SEARCH_BAR = "ComponentEspressoBookingModel",
	ALERTS_SECTION = "ComponentBubbleTilesModel",
	CRUISE_ENHANCEMENTS = "ComponentBubbleTilesModel",
	MARKETING_CENTER = "ComponentBubbleTilesModel",
	PROMOTIONS_SEARCH_HOMEPAGE = "ComponentPromoSearchModel",
	FEATURE_OFFERS = "Component2UpCardsModel",
	ARCHIVED_OFFERS = "Component50_50ImageAndTextModel",
	TRAINING_AND_CERTIFICATION = "ComponentTrainingAndCertificationModel",
	LOYALTY_REWARDS = "ComponentLoyaltyRewardsModel",
	SHIP_LOCATOR = "ComponentShipLocatorModel",
	LEADERS_MODULE = "ComponentGroupLeadersModel",
	SIDE_IMAGE_SECTION = "Component50_50SliderModel",
	BACKGROUND_HERO = "ComponentBackgroundHeroModel",
	LOGIN_FORM = "ComponentLoginFormModel",
	LOGIN_ASSISTANCE_MODAL = "ComponentLoginAssistanceModalModel",
	APP_FOOTER = "ComponentFooterModel",
	APP_NAVIGATION_FOOTER = "ComponentAppNavigationFooterModel",
	APP_LANGUAGES = "ComponentLanguagesModel",
	APP_DICTIONARY = "ComponentDictionaryModel",
	APP_GLOBAL_NAVIGATION = "ComponentGlobalNavigationModel",
	APP_NAVIGATION_SIDEBAR = "ComponentNavigationSidebarModel",
	APP_ALERTS = "ComponentAlertBannerList",
	APP_NOTIFICATIONS = "ComponentNotificationsList",
	PROMOTIONS_BRAND = "ComponentBrandSwitcherModel",
	ROW_OF_CARDS_SECTION = "ComponentRowOfCardsModel",
	SLIDER_CAROUSEL_SECTION = "ComponentCardSliderModel",
	IMAGE_BACKGROUND_SECTION = "ComponentImageBackgroundSectionModel",
	ICONS_GRID_SECTION = "ComponentIconsGridSectionModel",
	GALLERY_IMAGES_SECTION = "ComponentCollageGalleryModel",
	BUTTONS_CAROUSEL_SECTION = "ComponentButtonsCarouselSectionModel",
	PAGE_HEADER_SECTION = "ComponentIntroModel",
	SHIPS_SECTION = "ComponentFleetCardListingModel",
	INFINITE_CAROUSEL_SECTION = "ComponentCarouselGalleryModel",
	SIDE_CAROUSEL_SECTION = "ComponentSideCarouselSectionModel",
	ACTIVITY_DETAIL_TITLE_SECTION = "ComponentActivityDetailTitleSectionModel",
	STATIC_CAROUSEL_SECTION = "ComponentRowGalleryModel",
	FEATURED_OFFERS_INTRO_MODEL = "ComponentIntroModel",
	FEATURED_OFFERS_SEARCH_MODEL = "ComponentOffersSearchModel",
	TAGS_OFFER_NAME = "cruising-power:offer/offer-name",
	TAGS_OFFER_FOR = "cruising-power:offer/offer-for",
	TAGS_OFFER_COLLECTION = "cruising-power:offer/collection",
	OFFERS = "ComponentOffers",
	HERO_MODEL = "ComponentHeroModel",
	SLIDER_CAROUSEL_LIGTH_SECTION = "ComponentSliderCarouselLigthSectionModel",
	FILTER_BAR_MODEL = "ComponentFilterBarModel",
	DINING_CARD = "ComponentDiningCardModel",
	FULL_IMAGE_GALLERY = "ComponentFullImageGalleryModel",
	FOOTER_SECTION = "ComponentFooterSectionModel",
	SHIP_DETAILS = "ShipModel",
	SOS_ENTITY_DETAILS_MODEL = "ComponentEntityModel",
	OFFER_DETAILS = "ComponentOfferDetails",
	ENTERTAIMENT = "ComponentEntertainmentModel",
	SPECIALTY_DINING_CARD_TYPE = "cruising-power:type/specialty-dining",
	TOUR_3D_SECTION = "Component3dTourModel",
	VIDEO_SECTION = "ComponentVideoSectionModel",
	DESCRIPTION_MODEL = "ComponentDescriptionModel",
	FULL_WIDTH_BG_AND_TEXT = "ComponentFullWidthBgAndTextModel",
	SHIP_CTA_LABELS = "ComponentShipCtaLabelsModel",
	SPLIT_INTRO_MEDIA_MODEL = "ComponentSplitIntroMediaModel",
	TWO_CARD_LINK_LIST_MODEL = "Component2CardLinkListModel",
	ALERTS_HOME = "ComponentHeroModel",
	SEARCH_ITINERARY_HOME = "ItinerarySearchModel",
	FEATURED_ITINERARIES_CARDS = "Itinerary",
	SEARCH_ITINERARY_DETAIL = "ComponentMultiLinksModel",
	GROUP_DINING_SEARCH = "ComponentGroupDiningSearchModel",
	GROUP_DINING_TIMELINE = "ComponentGroupDiningTimelineModel",
	GROUP_DINING_GUEST_LIST = "ComponentGroupDiningGuestListModel",
	GROUP_DINING_TABLE_PANEL = "ComponentGroupDiningTablePanelModel",
	GROUP_DINING_TABLE_CONFIG = "ComponentGroupDiningTableConfigModel",
	GROUP_DINING_ALLOCATION_SUMMARY = "ComponentGroupDiningAllocationSummaryModel",
	RESOURCE_CARDS_ROW_MODEL = "ResourceCardsRowModel",
	SCROLLMODEL_50_50 = "Tradecomponent50_50ScrollModel",
	SIGNUP_FOR_TIPS = "TradecomponentsignuptipsModel",
	TRADE_BACKGROUND_HERO = "TradeComponentBackgroundHeroModel",
	TRADE_MULTI_LINK = "TradeComponentMultiLinkModel",
	TRADE_ASSET_LIBRARY = "TradeCategoryGroupModel",
	COMPONENT_MULTI_LINKS_MODEL = "ComponentMultiLinksModel"
}

export enum AEMSliderTypes {
	NO_SLIDE = "noSlide",
	NO_SLIDE_KEBAB_CASE = "no-slide",
	NO_IMAGE = "noImage",
	ACCOLADES_GALLERY = "accoladesGallery",
	FEATURED_IMAGE = "featuredImage",
	GALLERY = "gallery",
	SIDE_GALLERY = "sideGallery"
}

export enum AEMResourceTypes {
	OFFER = "offer",
	ITINERARY = "itinerary",
	ALL_GALLERY_CARDS = "allGalleryCards"
}

export interface AEMSliderModelProps extends MediaGalleryItem {
	type: string
	sliderType: AEMSliderTypes
	contentAlignment: MediaPositionType
	sideNavLabel: string
	additionalInfoHeaders: string[]
	backgroundImage: {
		path: string
		title: string
		description: string
		width: number
		height: number
	}
	gallery: Icon[]
	title: string
	link?: LinkItem
	fullScreen?: boolean
	subTitle?: string
	highlightsList?: string[]
	highlightsLabel?: string
	contentReference?: AEMSliderModelProps[]
	backgroundColor?: string
	sideGalleryItems?: MediaGalleryItem[]
	onClick?: () => void
}

export interface MediaGalleryItem {
	featuredImage: AEMImageData
	headline: string
	headlineDescription: AEMDescriptionType
	additionalInfo: string[]
	primaryLinkText?: string
	primaryLinkUrl?: string
	primaryLinkType?: string
	primaryLinkAppId?: string
	primaryLinkToolCode?: string
	primaryLinkRetainBasePath?: boolean
}

export interface Icon {
	publishUrl: string
	path: string
	title: string
	description: string
	color?: string
}

export interface BrandHeroType {
	type: string
	brandName: string
	brandCode: string
	icon: Icon
}

export interface DescriptionModelProps {
	type?: string
	resource?: string
	title?: string
	description?: AEMDescriptionType
	backgroundImage?: AEMImageData
	backgroundColor?: boolean
	currentDeck?: string
	currentProfileCode?: string
	deckPlan?: DeckContent[]
	dateRangeOptions?: DateRangeOptions[]
}

export interface AEMPromotionOfferItems {
	items: AEMPromotionOffer[]
}
